import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Briefcase, Plus, Pencil, Power, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobTitlesManagement = () => {
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: '', department: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/job-titles`);
      setTitles(res.data?.jobTitles || []);
    } catch (e) {
      toast.error("Impossible de charger les postes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ label: '', department: '' });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ label: t.label, department: t.department || '' });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.label.trim() || form.label.trim().length < 2) {
      toast.error('Intitulé du poste requis');
      return;
    }
    setSaving(true);
    try {
      const payload = { label: form.label.trim(), department: form.department.trim() };
      if (editing) {
        await axios.put(`${API}/settings/job-titles/${editing.id}`, payload);
        toast.success('Poste mis à jour');
      } else {
        await axios.post(`${API}/settings/job-titles`, payload);
        toast.success('Poste ajouté');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      if (t.isActive) {
        await axios.delete(`${API}/settings/job-titles/${t.id}`);
        toast.success(`« ${t.label} » désactivé`);
      } else {
        await axios.put(`${API}/settings/job-titles/${t.id}`, { isActive: true });
        toast.success(`« ${t.label} » réactivé`);
      }
      load();
    } catch (e) {
      toast.error("Action impossible");
    }
  };

  const groups = Object.entries(
    titles.reduce((acc, t) => {
      const key = t.department || 'Sans département';
      (acc[key] = acc[key] || []).push(t);
      return acc;
    }, {})
  ).sort((a, b) => a[0].localeCompare(b[0], 'fr'));

  return (
    <Card data-testid="job-titles-management">      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <Briefcase className="w-5 h-5 text-gray-700" />
            Postes dans l&apos;entreprise
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Liste des fonctions proposées sur les fiches du personnel. Le poste décrit la
            fonction réelle ; il n&apos;a aucun effet sur les droits d&apos;accès.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="job-title-add-btn" className="bg-[#1db584] hover:bg-[#16a075]">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un poste
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : titles.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-8 text-center">
            Aucun poste défini. Ajoutez-en un pour pouvoir créer des fiches du personnel.
          </p>
        ) : (
          <div className="space-y-4">
            {groups.map(([dept, items]) => (
              <div key={dept}>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  {dept} <span className="font-normal normal-case">· {items.length}</span>
                </p>
                <div className="divide-y border rounded-lg">
                  {items.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
                data-testid={`job-title-row-${t.id}`}
              >
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${t.isActive ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                    {t.label}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t.usageCount > 0 ? `${t.usageCount} personne(s)` : 'Aucune fiche'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!t.isActive && <Badge variant="outline" className="text-xs">Désactivé</Badge>}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`job-title-edit-${t.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(t)}
                    className={t.isActive ? 'text-red-600' : 'text-emerald-600'}
                    data-testid={`job-title-toggle-${t.id}`}
                  >
                    <Power className="w-4 h-4" />
                  </Button>
                </div>
              </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="job-title-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le poste' : 'Nouveau poste'}</DialogTitle>
            <DialogDescription>
              Renommer un poste met à jour automatiquement toutes les fiches concernées.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="job-title-label">Intitulé du poste *</Label>
              <Input
                id="job-title-label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Ex. : Responsable Exploitation"
                data-testid="job-title-label-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="job-title-dept">Département (optionnel)</Label>
              <Input
                id="job-title-dept"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                placeholder="Ex. : OPERATIONS"
                data-testid="job-title-department-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="job-title-save-btn" className="bg-[#1db584] hover:bg-[#16a075]">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enregistrement…</> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default JobTitlesManagement;
